import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Brain,
  Search,
  Download,
  Archive,
  MessageSquare,
  Clock,
  User,
  ChevronRight,
  ChevronDown,
  Filter,
  Calendar,
  RefreshCw,
  FileText,
  Loader2,
  AlertCircle,
  TrendingUp,
  Users,
  Layers,
} from "lucide-react";
import AdminSidebar from "../components/layout/AdminSidebar";

const AILogsPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Dashboard stats
  const [stats, setStats] = useState(null);
  
  // Threads list
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsPagination, setThreadsPagination] = useState({ skip: 0, limit: 20, total: 0 });
  
  // Search/logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ skip: 0, limit: 50, total: 0 });
  
  // Filters
  const [filters, setFilters] = useState({
    module: "",
    role: "",
    keyword: "",
    startDate: "",
    endDate: "",
    includeArchived: false,
  });
  
  // Thread detail modal
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadModalOpen, setThreadModalOpen] = useState(false);
  
  // Modules list
  const [modules, setModules] = useState([]);
  
  // Fetch dashboard stats
  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/ai-logs/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard stats");
    }
  }, [token]);
  
  // Fetch modules list
  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/ai-logs/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModules(response.data.modules || []);
    } catch (error) {
      console.error("Failed to fetch modules:", error);
    }
  }, [token]);
  
  // Fetch threads
  const fetchThreads = useCallback(async (skip = 0) => {
    setThreadsLoading(true);
    try {
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: "20",
        include_archived: filters.includeArchived.toString(),
      });
      if (filters.module) params.append("module", filters.module);
      
      const response = await axios.get(`${API}/admin/ai-logs/threads?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThreads(response.data.threads || []);
      setThreadsPagination({
        skip,
        limit: 20,
        total: response.data.total,
        hasMore: response.data.has_more,
      });
    } catch (error) {
      toast.error("Failed to load threads");
    } finally {
      setThreadsLoading(false);
    }
  }, [token, filters.module, filters.includeArchived]);
  
  // Fetch logs with search
  const fetchLogs = useCallback(async (skip = 0) => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: "50",
        include_archived: filters.includeArchived.toString(),
      });
      if (filters.module) params.append("module", filters.module);
      if (filters.role) params.append("role", filters.role);
      if (filters.keyword) params.append("keyword", filters.keyword);
      if (filters.startDate) params.append("start_date", new Date(filters.startDate).toISOString());
      if (filters.endDate) params.append("end_date", new Date(filters.endDate).toISOString());
      
      const response = await axios.get(`${API}/admin/ai-logs/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(response.data.logs || []);
      setLogsPagination({
        skip,
        limit: 50,
        total: response.data.total,
        hasMore: response.data.has_more,
      });
    } catch (error) {
      toast.error("Failed to search logs");
    } finally {
      setLogsLoading(false);
    }
  }, [token, filters]);
  
  // Fetch thread detail
  const fetchThreadDetail = async (threadId) => {
    try {
      const response = await axios.get(`${API}/admin/ai-logs/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedThread(response.data);
      setThreadMessages(response.data.messages || []);
      setThreadModalOpen(true);
    } catch (error) {
      toast.error("Failed to load thread");
    }
  };
  
  // Export logs
  const handleExport = async (format = "json") => {
    try {
      const response = await axios.post(
        `${API}/admin/ai-logs/export`,
        {
          format,
          include_archived: filters.includeArchived,
          module: filters.module || null,
          start_date: filters.startDate ? new Date(filters.startDate).toISOString() : null,
          end_date: filters.endDate ? new Date(filters.endDate).toISOString() : null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: format === "csv" ? "blob" : "json",
        }
      );
      
      if (format === "csv") {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ai_logs_export_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ai_logs_export_${new Date().toISOString().split("T")[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      
      toast.success(`Exported ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error("Export failed");
    }
  };
  
  // Trigger archival
  const handleArchive = async () => {
    if (!window.confirm("Archive all logs older than 120 days?")) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/ai-logs/archive?days_threshold=120`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchDashboardStats();
    } catch (error) {
      toast.error("Archive failed");
    }
  };
  
  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchDashboardStats(), fetchModules()]);
      setLoading(false);
    };
    init();
  }, [fetchDashboardStats, fetchModules]);
  
  // Load data when tab changes
  useEffect(() => {
    if (activeTab === "threads") {
      fetchThreads(0);
    } else if (activeTab === "search") {
      fetchLogs(0);
    }
  }, [activeTab, fetchThreads, fetchLogs]);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };
  
  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };
  
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-100 text-red-700";
      case "board": return "bg-blue-100 text-blue-700";
      case "property_manager": return "bg-green-100 text-green-700";
      case "owner": return "bg-amber-100 text-amber-700";
      case "system": return "bg-purple-100 text-purple-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };
  
  const getModuleName = (moduleId) => {
    const mod = modules.find(m => m.id === moduleId);
    return mod ? mod.name : moduleId;
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
    <div className="min-h-screen bg-background" data-testid="ai-logs-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="w-6 h-6 text-primary" />
              <div>
                <h1 className="font-serif text-xl font-semibold">AI Conversation Logs</h1>
                <p className="text-sm text-muted-foreground">System-wide AI interaction audit trail</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport("json")}
                data-testid="export-json-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export JSON
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport("csv")}
                data-testid="export-csv-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              {user?.role === "admin" && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleArchive}
                  data-testid="archive-btn"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Old
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="threads" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              Threads
            </TabsTrigger>
            <TabsTrigger value="search" className="gap-2">
              <Search className="w-4 h-4" />
              Search Logs
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {stats && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100">
                          <MessageSquare className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.total_logs}</p>
                          <p className="text-xs text-muted-foreground">Total Logs</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-100">
                          <Clock className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.today_logs}</p>
                          <p className="text-xs text-muted-foreground">Today</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-100">
                          <Layers className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.unique_threads_30d}</p>
                          <p className="text-xs text-muted-foreground">Threads (30d)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-100">
                          <Archive className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stats.archived_logs}</p>
                          <p className="text-xs text-muted-foreground">Archived</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* By Module */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Logs by Module (30 days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {stats.logs_by_module.length > 0 ? (
                        <div className="space-y-3">
                          {stats.logs_by_module.map((item) => (
                            <div key={item.module} className="flex items-center justify-between">
                              <span className="text-sm">{getModuleName(item.module)}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full"
                                    style={{
                                      width: `${Math.min(100, (item.count / Math.max(...stats.logs_by_module.map(m => m.count))) * 100)}%`
                                    }}
                                  />
                                </div>
                                <span className="text-sm font-medium w-12 text-right">{item.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No data yet</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* By Role */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Logs by Role (30 days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {stats.logs_by_role.length > 0 ? (
                        <div className="space-y-3">
                          {stats.logs_by_role.map((item) => (
                            <div key={item.role} className="flex items-center justify-between">
                              <Badge className={getRoleBadgeColor(item.role)}>
                                {item.role}
                              </Badge>
                              <span className="text-sm font-medium">{item.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No data yet</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Top Users */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Top AI Users (30 days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats.top_users.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {stats.top_users.map((item, index) => (
                          <div key={item.user_id} className="text-center p-3 rounded-lg bg-muted/50">
                            <p className="text-lg font-bold">{item.count}</p>
                            <p className="text-xs text-muted-foreground truncate">{item.user_id}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No data yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Threads Tab */}
          <TabsContent value="threads">
            <div className="space-y-4">
              {/* Filters */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <Select
                      value={filters.module || "all"}
                      onValueChange={(v) => setFilters({ ...filters, module: v === "all" ? "" : v })}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="All Modules" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Modules</SelectItem>
                        {modules.map((mod) => (
                          <SelectItem key={mod.id} value={mod.id}>{mod.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchThreads(0)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Threads List */}
              {threadsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : threads.length > 0 ? (
                <div className="space-y-3">
                  {threads.map((thread) => (
                    <Card
                      key={thread.thread_id}
                      className="cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => fetchThreadDetail(thread.thread_id)}
                      data-testid={`thread-${thread.thread_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={getRoleBadgeColor(thread.role)}>
                                {thread.role}
                              </Badge>
                              <Badge variant="outline">{getModuleName(thread.module)}</Badge>
                              <span className="text-xs text-muted-foreground">
                                {thread.message_count} messages
                              </span>
                            </div>
                            <p className="text-sm font-medium truncate">
                              {truncateText(thread.first_message, 80)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Started: {formatDate(thread.started_at)} | Last: {formatDate(thread.last_activity)}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Pagination */}
                  <div className="flex justify-center gap-2 pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={threadsPagination.skip === 0}
                      onClick={() => fetchThreads(Math.max(0, threadsPagination.skip - 20))}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground self-center">
                      {threadsPagination.skip + 1} - {Math.min(threadsPagination.skip + threads.length, threadsPagination.total)} of {threadsPagination.total}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!threadsPagination.hasMore}
                      onClick={() => fetchThreads(threadsPagination.skip + 20)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <MessageSquare className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">No conversation threads found</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Search Tab */}
          <TabsContent value="search">
            <div className="space-y-4">
              {/* Search Filters */}
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground">Keyword</label>
                      <Input
                        placeholder="Search..."
                        value={filters.keyword}
                        onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                        data-testid="search-keyword"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Module</label>
                      <Select
                        value={filters.module || "all"}
                        onValueChange={(v) => setFilters({ ...filters, module: v === "all" ? "" : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Modules</SelectItem>
                          {modules.map((mod) => (
                            <SelectItem key={mod.id} value={mod.id}>{mod.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Role</label>
                      <Select
                        value={filters.role || "all"}
                        onValueChange={(v) => setFilters({ ...filters, role: v === "all" ? "" : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Roles</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="board">Board</SelectItem>
                          <SelectItem value="property_manager">Property Manager</SelectItem>
                          <SelectItem value="owner">Owner</SelectItem>
                          <SelectItem value="system">System</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Start Date</label>
                      <Input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-4">
                    <Button onClick={() => fetchLogs(0)} data-testid="search-btn">
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setFilters({ module: "", role: "", keyword: "", startDate: "", endDate: "", includeArchived: false })}
                    >
                      Clear Filters
                    </Button>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={filters.includeArchived}
                        onChange={(e) => setFilters({ ...filters, includeArchived: e.target.checked })}
                      />
                      Include Archived
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Search Results */}
              {logsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : logs.length > 0 ? (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <Card key={log.log_id} data-testid={`log-${log.log_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={getRoleBadgeColor(log.role)}>{log.role}</Badge>
                              <Badge variant="outline">{getModuleName(log.module)}</Badge>
                              <Badge variant="secondary">{log.interaction_type}</Badge>
                              {log.archived && <Badge variant="destructive">Archived</Badge>}
                            </div>
                            <div className="space-y-2">
                              <div className="bg-muted/50 p-2 rounded text-sm">
                                <span className="text-xs text-muted-foreground">Input:</span>
                                <p>{truncateText(log.input_text, 200)}</p>
                              </div>
                              <div className="bg-primary/5 p-2 rounded text-sm">
                                <span className="text-xs text-muted-foreground">Output:</span>
                                <p>{truncateText(log.output_text, 200)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>{formatDate(log.timestamp)}</span>
                              <span>User: {log.user_id}</span>
                              {log.model_used && <span>Model: {log.model_used}</span>}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Pagination */}
                  <div className="flex justify-center gap-2 pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={logsPagination.skip === 0}
                      onClick={() => fetchLogs(Math.max(0, logsPagination.skip - 50))}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground self-center">
                      {logsPagination.skip + 1} - {Math.min(logsPagination.skip + logs.length, logsPagination.total)} of {logsPagination.total}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!logsPagination.hasMore}
                      onClick={() => fetchLogs(logsPagination.skip + 50)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Search className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">No logs found. Try adjusting your filters.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Thread Detail Modal */}
      <Dialog open={threadModalOpen} onOpenChange={setThreadModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Conversation Thread
              {selectedThread && (
                <Badge variant="outline" className="ml-2">
                  {selectedThread.message_count} messages
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {threadMessages.map((msg, index) => (
              <div key={msg.log_id || index} className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge className={getRoleBadgeColor(msg.role)} variant="outline">
                    {msg.role}
                  </Badge>
                  <span>{formatDate(msg.timestamp)}</span>
                  {msg.model_used && <span>({msg.model_used})</span>}
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">User Input:</p>
                  <p className="text-sm whitespace-pre-wrap">{msg.input_text}</p>
                </div>
                <div className="bg-primary/5 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">AI Response:</p>
                  <p className="text-sm whitespace-pre-wrap">{msg.output_text}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </AdminSidebar>
  );
};

export default AILogsPage;
